import { Fn, Fn2 }			from "ts-base/fn";
import { Sync }				from "ts-base/sync";
import { Equal }			from "ts-base/equal";
import { Comparator }		from "ts-base/comparator";

import { DateOffset }		from "ts-base/time/dateOffset";
import { WeekdayName }		from "ts-base/time/weekdayName";
import { WeekdayNumber }	from "ts-base/time/weekdayNumber";
import { Duration }			from "ts-base/time/duration";

export namespace Dates {
	/** never mutate this - sadly, it can't just be frozen */
	export const epoch	= new Date(0);

	export const now:Sync<Date>	= ()	=> new Date();

	/** how much time has passed since some start date until now */
	export const elapsedSince	= (startedAt:Date):Duration	=>
		Duration.brand(Date.now() - startedAt.getTime());

	export const clone	= (it:Date):Date	=> new Date(it.getTime());

	export const validOrNull	= (it:Date):Date|null	=>
		isValid(it) ? it : null;

	export const isValid	= (it:Date):boolean	=> !isValid(it);
	export const isInvalid	= (it:Date):boolean	=> Number.isNaN(it.getTime());

	//-----------------------------------------------------------------------------
	//## typeclasses

	export const equal:Equal<Date>			= Equal.builtinBy(it => it.getTime());
	export const compare:Comparator<Date>	= Comparator.builtin;

	//------------------------------------------------------------------------------

	export type BuiltinZone	= Readonly<{
		earlier:		(base:Date, offset:DateOffset.Partial)	=> Date,
		later:			(base:Date, offset:DateOffset.Partial)	=> Date,

		past:			(offset:DateOffset.Partial)	=> Date,
		future:			(offset:DateOffset.Partial)	=> Date,

		weekdayName:	(self:Date)	=> WeekdayName,
		weekdayNumber:	(self:Date)	=> WeekdayNumber,
	}>;

	const builtinZone	= (
		shift:Fn2<Date, DateOffset, Date>,
		weekdayNumber:Fn<Date, WeekdayNumber>,
	):BuiltinZone	=> ({
		earlier:		(base:Date, offset:DateOffset.Partial):Date	=> shift(base, DateOffset.negate(DateOffset.fromPartial(offset))),
		later:			(base:Date, offset:DateOffset.Partial):Date	=> shift(base, DateOffset.fromPartial(offset)),

		past:			(offset:DateOffset.Partial):Date			=> shift(now(), DateOffset.negate(DateOffset.fromPartial(offset))),
		future:			(offset:DateOffset.Partial):Date			=> shift(now(), DateOffset.fromPartial(offset)),

		weekdayName:	(self:Date)	=> WeekdayName.fromWeekDayNumber(weekdayNumber(self)),
		weekdayNumber:	weekdayNumber,
	});

	export const local:BuiltinZone	=
		builtinZone(
			(base:Date, self:DateOffset):Date	=> {
				const out	= clone(base);
				out.setFullYear(		out.getFullYear()		+ self.years);
				out.setMonth(			out.getMonth()			+ self.months,		out.getDate()		+ self.days);
				// out.setDate(			out.getDate()			+ self.days);
				out.setHours(			out.getHours()			+ self.hours);
				out.setMinutes(			out.getMinutes()		+ self.minutes);
				out.setSeconds(			out.getSeconds()		+ self.seconds);
				out.setMilliseconds(	out.getMilliseconds()	+ self.millis);
				return out;
			},
			it => WeekdayNumber.fromSundayBasedIndex(it.getDay())
		);

	export const utc:BuiltinZone	=
		builtinZone(
			(base:Date, self:DateOffset):Date	=> {
				const out	= clone(base);
				out.setUTCFullYear(		out.getUTCFullYear()		+ self.years);
				out.setUTCMonth(		out.getUTCMonth()			+ self.months,	out.getUTCDate()	+ self.days);
				// out.setUTCDate(			out.getUTCDate()			+ self.days);
				out.setUTCHours(		out.getUTCHours()			+ self.hours);
				out.setUTCMinutes(		out.getUTCMinutes()			+ self.minutes);
				out.setUTCSeconds(		out.getUTCSeconds()			+ self.seconds);
				out.setUTCMilliseconds(	out.getUTCMilliseconds()	+ self.millis);
				return out;
			},
			it => WeekdayNumber.fromSundayBasedIndex(it.getUTCDay())
		);
}
